<template>
  <div class="quiz-wrapper">

    <!-- Aucune question chargée -->
    <div v-if="questions.length === 0" class="quiz-empty">
      <div class="empty-icon">📝</div>
      <p>Aucune question n'a été chargée.</p>
    </div>

    <!-- Jeu en cours -->
    <div v-else-if="gameState === 'playing' && currentQuestion" class="quiz-container">

      <!-- En-tête progression -->
      <div class="quiz-header">
        <div class="niveau-dots">
          <span v-for="n in 5" :key="n" class="niveau-dot" :class="{
            'dot-done': n < currentNiveau,
            'dot-current': n === currentNiveau,
            'dot-pending': n > currentNiveau
          }">{{ n }}</span>
        </div>
        <p class="progress-label">Question {{ currentQuestionIndex + 1 }}/4 — Niveau {{ currentNiveau }}</p>
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>

      <!-- Question -->
      <div class="question-block">
        <p class="question-text">{{ currentQuestion.question }}</p>
      </div>

      <!-- Choix de réponses -->
      <div class="choices-grid">
        <button v-for="(choix, i) in currentQuestion.choix" :key="i" class="choice-btn" :class="getChoiceClass(choix)"
          :disabled="answered" @click="selectAnswer(choix)">
          <span class="choice-letter">{{ letters[i] }}</span>
          <span class="choice-text">{{ choix }}</span>
        </button>
      </div>

      <!-- Feedback immédiat -->
      <transition name="feedback-slide">
        <div v-if="answered" class="feedback-block">
          <div class="feedback-message" :class="isCorrect ? 'feedback-ok' : 'feedback-ko'">
            <span v-if="isCorrect">✓ Bonne réponse !</span>
            <span v-else>✗ Incorrect — La bonne réponse était : <strong>{{ currentQuestion.reponse }}</strong></span>
          </div>
          <button class="btn-next" @click="nextQuestion">
            {{ isLastQuestion ? 'Voir le résultat' : 'Question suivante →' }}
          </button>
        </div>
      </transition>

    </div>

    <!-- Niveau réussi (interstitiel) -->
    <transition name="fade">
      <div v-if="gameState === 'levelCleared'" class="level-cleared-wrapper">
        <div class="level-cleared-card">
          <div class="cleared-icon">✓</div>
          <p class="cleared-eyebrow">Niveau {{ niveauAtteint }}</p>
          <h2 class="cleared-title">Réussi !</h2>
          <p class="cleared-score">{{ lastLevelScore }}/4 bonnes réponses</p>
          <button class="btn-next" @click="continueToNextLevel">
            Niveau {{ niveauAtteint + 1 }} →
          </button>
        </div>
      </div>
    </transition>

    <!-- Écran final -->
    <transition name="fade">
      <div v-if="gameState === 'finished'" class="result-wrapper">
        <div class="result-card">

          <div class="result-section">
            <p class="result-eyebrow">Votre niveau estimé</p>
            <div class="result-main">
              <span class="result-num">Niveau {{ niveauAffiche }}</span>
            </div>
            <h2>Étape suivante</h2>
            <h3>Essayez notre cours de japonais !</h3>
            <p>L’ADLCJ, forte de 30 ans d’expérience, propose plusieurs niveaux de cours.</p>
            <h3>À propos du cours d’essai</h3>
            <ul>
              <li>Durée : 2h</li>
              <li>Tarif : 16€</li>
              <li>Aucune obligation d’inscription ultérieure</li>
            </ul>
            <div class="btnWithLabel">
              <span class="btnLabel btnLabel2">Réservez votre cours d'essai</span>
              <ButtonSecondary
                label="Je réserve"
                variant="red"
                href="https://www.helloasso.com/associations/adlcj/boutiques/adlcj-cours-de-japonais-2026-2027"
                target="_blank"
              />
            </div>
          </div>

          <button class="btn-restart" @click="restart">↺ Recommencer</button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { questions } from '../data/questions.js'
import ButtonSecondary from './ButtonSecondary.vue'

export default {
  name: 'NiveauQuiz',
  components: { ButtonSecondary },

  data() {
    return {
      questions: questions,
      letters: ['A', 'B', 'C', 'D'],
      gameState: 'playing',
      currentNiveau: 1,
      currentQuestionIndex: 0,
      score: 0,
      lastLevelScore: 0,
      niveauAtteint: 0,
      totalScore: 0,
      selectedAnswer: null,
      answered: false,
      isCorrect: false,
    }
  },

  computed: {
    questionsForCurrentNiveau() {
      return this.questions.filter(q => q.niveau === this.currentNiveau)
    },
    currentQuestion() {
      return this.questionsForCurrentNiveau[this.currentQuestionIndex] || null
    },
    progressPercent() {
      return ((this.currentQuestionIndex + 1) / 4) * 100
    },
    isLastQuestion() {
      return this.currentQuestionIndex === 3
    },
    niveauAffiche() {
      return Math.max(this.niveauAtteint, 1)
    },
  },

  methods: {
    selectAnswer(choix) {
      if (this.answered) return
      this.selectedAnswer = choix
      this.isCorrect = choix === this.currentQuestion.reponse
      if (this.isCorrect) {
        this.score++
        this.totalScore++
      }
      this.answered = true
    },
    getChoiceClass(choix) {
      if (!this.answered) return ''
      if (choix === this.currentQuestion.reponse) return 'choice-correct'
      if (choix === this.selectedAnswer) return 'choice-incorrect'
      return 'choice-dimmed'
    },
    nextQuestion() {
      if (this.currentQuestionIndex < 3) {
        this.currentQuestionIndex++
        this.selectedAnswer = null
        this.answered = false
        this.isCorrect = false
      } else {
        this.evaluateNiveau()
      }
    },
    evaluateNiveau() {
      if (this.score >= 3) {
        this.niveauAtteint = this.currentNiveau
        this.lastLevelScore = this.score
        if (this.currentNiveau < 5) {
          this.gameState = 'levelCleared'
        } else {
          this.gameState = 'finished'
        }
      } else {
        this.gameState = 'finished'
      }
    },
    continueToNextLevel() {
      this.currentNiveau++
      this.currentQuestionIndex = 0
      this.score = 0
      this.selectedAnswer = null
      this.answered = false
      this.isCorrect = false
      this.gameState = 'playing'
    },
    getLevelBadgeClass(n) {
      if (n <= this.niveauAtteint) return 'badge-passed'
      if (n === this.niveauAtteint + 1 && this.niveauAtteint < 5) return 'badge-failed'
      return 'badge-untouched'
    },
    getLevelBadgeIcon(n) {
      if (n <= this.niveauAtteint) return '✓'
      if (n === this.niveauAtteint + 1 && this.niveauAtteint < 5) return '✗'
      return '—'
    },
    restart() {
      this.currentNiveau = 1
      this.currentQuestionIndex = 0
      this.score = 0
      this.lastLevelScore = 0
      this.selectedAnswer = null
      this.answered = false
      this.isCorrect = false
      this.gameState = 'playing'
      this.niveauAtteint = 0
      this.totalScore = 0
    },
  },
}
</script>

<style lang="scss" scoped>
$correct: #27ae60;
$correct-light: #eafaf1;
$incorrect: $accentColor;
$incorrect-light: #fdf0f0;

// ---- WRAPPER ----
.quiz-wrapper {
  max-width: 620px;
  margin: 0 auto;
  padding: 2rem 1rem;
  font-family: 'Source Sans 3', sans-serif;
  min-height: 55vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

// ---- ÉTAT VIDE ----
.quiz-empty {
  text-align: center;
  color: $subText;
  padding: 3rem 1rem;

  .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
  }
}

// ---- EN-TÊTE ----
.quiz-header {
  margin-bottom: 1.75rem;
}

.niveau-dots {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.niveau-dot {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &.dot-done {
    background: $accentColor;
    color: #fff;
    border-color: $accentColor;
  }

  &.dot-current {
    background: $accentGold;
    color: #fff;
    border-color: $accentGold;
    transform: scale(1.18);
    box-shadow: 0 0 0 4px rgba($accentGold, 0.25);
  }

  &.dot-pending {
    background: #fff;
    color: #ccc;
    border-color: #e0e0e0;
  }
}

.progress-label {
  text-align: center;
  font-size: 0.9rem;
  color: $subText;
  margin-bottom: 0.5rem;
}

.progress-track {
  height: 6px;
  background: #e8e8e8;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: $accentGold;
  border-radius: 3px;
  transition: width 0.4s ease;
}

// ---- QUESTION ----
.question-block {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 1.6rem 1.4rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}

.question-text {
  font-size: 1.15rem;
  font-weight: 600;
  color: $textColor;
  line-height: 1.65;
  margin: 0;
  text-align: center;
}

// ---- CHOIX ----
.choices-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
}

.choice-btn {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 0.98rem;
  font-weight: 500;
  color: $textColor;
  text-align: left;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.1s ease;
  width: 100%;

  &:hover:not(:disabled) {
    border-color: $accentGold;
    background: rgba($accentGold, 0.06);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
  }

  &.choice-correct {
    border-color: $correct;
    background: $correct-light;
    color: darken($correct, 8%);

    .choice-letter {
      background: $correct;
      color: #fff;
    }
  }

  &.choice-incorrect {
    border-color: $incorrect;
    background: $incorrect-light;
    color: $incorrect;

    .choice-letter {
      background: $incorrect;
      color: #fff;
    }
  }

  &.choice-dimmed {
    opacity: 0.4;
  }
}

.choice-letter {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease;
}

.choice-text {
  flex: 1;
  line-height: 1.4;
}

// ---- FEEDBACK ----
.feedback-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  margin-top: 0.25rem;
}

.feedback-message {
  width: 100%;
  padding: 0.9rem 1.2rem;
  border-radius: 8px;
  font-size: 0.98rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.5;

  &.feedback-ok {
    background: $correct-light;
    color: darken($correct, 8%);
    border: 1px solid rgba($correct, 0.35);
  }

  &.feedback-ko {
    background: $incorrect-light;
    color: $incorrect;
    border: 1px solid rgba($incorrect, 0.3);
  }
}

// ---- BOUTONS PARTAGÉS ----
.btn-next {
  padding: 0.72rem 1.8rem;
  background: $accentColor;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover {
    background: darken($accentColor, 8%);
    transform: translateY(-1px);
  }
}

// ---- NIVEAU RÉUSSI (interstitiel) ----
.level-cleared-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 55vh;
}

.level-cleared-card {
  background: #fff;
  border-radius: 16px;
  padding: 2.5rem 2rem;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
}

.cleared-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: $correct;
  color: #fff;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.cleared-eyebrow {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: $subText;
  margin-bottom: 0.3rem;
}

.cleared-title {
  font-size: 2rem;
  font-weight: 700;
  color: $correct;
  margin-bottom: 0.5rem;
}

.cleared-score {
  font-size: 1rem;
  color: $subText;
  margin-bottom: 1.5rem;
}

// ---- RÉSULTAT FINAL ----
.result-wrapper {
  max-width: 620px;
  margin: 0 auto;
  padding: 0.5rem 1rem 2rem;
}

.result-card {
  text-align: center;
}

.result-eyebrow {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: $subText;
  margin-bottom: 0.5rem;
}

.result-main {
  display: flex;
  justify-content: center;
  margin: 0.4rem 0 1.75rem;
}

.result-num {
  font-size: 3.5rem;
  font-weight: 700;
  color: $accentGold;
  line-height: 1;
}

.result-section {
  border: 2px solid $accentGold;
  border-radius: 12px;
  background-origin: border-box;
  background-clip: padding-box, border-box;
  padding: 1.5rem 1.75rem;
  text-align: left;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.3rem;
    font-weight: 700;
    color: $textColor;
    margin-top: 0;
    margin-bottom: 0.75rem;
  }

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: $textColor;
    margin: 1rem 0 0.3rem;
  }

  p {
    font-size: 0.95rem;
    line-height: 1.6;
    color: $textColor;
    margin: 0.25rem 0;
  }

  ul {
    padding-left: 1.2rem;
    margin: 0.4rem 0 0;

    li {
      font-size: 0.95rem;
      line-height: 1.7;
      color: $textColor;
    }
  }
}

.btnWithLabel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 1.25rem;
}

.btnLabel {
  font-size: 0.9rem;
  font-weight: 600;
  color: $textColor;
  display: flex;
  align-items: center;
  gap: 5px;

  &::before {
    content: "";
    display: inline-block;
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }

  &.btnLabel2::before {
    background-image: url('../assets/image/icons/redRight.svg');
  }
}

.result-levels {
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 1.5rem;

  @media (max-width: 360px) {
    gap: 0.35rem;
  }
}

.result-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;

  .badge-num {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 700;

    @media (max-width: 360px) {
      width: 36px;
      height: 36px;
      font-size: 0.9rem;
    }
  }

  .badge-icon {
    font-size: 0.7rem;
    font-weight: 700;
  }

  &.badge-passed {
    .badge-num {
      background: $correct;
      color: #fff;
    }

    .badge-icon {
      color: $correct;
    }
  }

  &.badge-failed {
    .badge-num {
      background: $incorrect;
      color: #fff;
    }

    .badge-icon {
      color: $incorrect;
    }
  }

  &.badge-untouched {
    .badge-num {
      background: #f0f0f0;
      color: #bbb;
    }

    .badge-icon {
      color: #bbb;
    }
  }
}

.result-message {
  font-size: 1rem;
  color: $subText;
  line-height: 1.55;
  margin-bottom: 1.5rem;
}

.btn-restart {
  padding: 0.75rem 2rem;
  background: transparent;
  color: #888;
  border: 2px solid #ccc;
  border-radius: 8px;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.1s ease;

  &:hover {
    background: #f0f0f0;
    color: #555;
    transform: translateY(-1px);
  }
}

// ---- TRANSITIONS ----
.feedback-slide-enter-active {
  animation: slideUp 0.28s ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(14px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}


@media (max-width: 700px) {
  .result-main {
    margin-bottom: 0.75rem;

    .result-num {
      font-size: 2rem;
    }
  }

  .btnLabel {
    display: block;

    &::before {
      display: inline-block;
      vertical-align: middle;
      margin-right: 4px;
    }
  }
}
</style>
